import { useQuery } from "@tanstack/react-query";
import api from "../../api";
import type { InvestmentSummaryItem } from "../../api/types.ts";
import styles from "./BiodiversityCreditsSection.module.css";

const formatSquareMetres = (totalAreaInSquareMetres: number) => {
  return new Intl.NumberFormat("en-GB", {
    maximumFractionDigits: 0,
  }).format(totalAreaInSquareMetres);
};

function BiodiversityCreditsSection() {
  const { data: summary = [], isPending } = useQuery<InvestmentSummaryItem[]>({
    queryKey: ["investmentSummary"],
    queryFn: api.get.investmentSummary,
  });

  const totalAreaSupportedM2 = summary.reduce(
    (acc, row) => acc + row.totalAreaSupportedM2,
    0,
  );
  const totalAreaInSquareMetres = formatSquareMetres(totalAreaSupportedM2);

  return (
    <section
      className={styles.section}
      aria-labelledby="biodiversity-credits-section-title"
    >
      <div className={styles.container}>
        <div className={styles.topRow}>
          <div className={styles.metricBlock}>
            <h2
              id="biodiversity-credits-section-title"
              className={styles.title}
            >
              What are biodiversity credits?
            </h2>
            {isPending ? (
              <div
                className={styles.metricValueSkeleton}
                data-testid="sqm-metric-skeleton"
                aria-hidden="true"
              />
            ) : (
              <p className={styles.metricValue} data-testid="sqm-metric-value">
                {totalAreaInSquareMetres}
              </p>
            )}
            <p className={styles.metricLabel}>Total sqm protected so far</p>
          </div>

          <div className={styles.copyBlock}>
            <p className={styles.copyParagraph}>
              Biodiversity underpins our economy, wellbeing and prosperity - yet
              it is declining at an unprecedented rate. From degraded ecosystems
              to increasing natural disasters, the risks of inaction are
              becoming impossible for businesses to ignore.
            </p>
            <p className={styles.copyParagraph}>
              Biodiversity credits offer a practical, positive way for companies
              to respond. A voluntary biodiversity credit represents an area of
              land within a project where actions are being taken to actively
              protect or restore ecosystems. Each credit is mapped,
              scientifically verified and secured for 30 years.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default BiodiversityCreditsSection;
